/**
 * Maps import/UI display field names to their location on the asset_devices table.
 *
 * - Top-level paths (e.g., 'id') resolve to the column `d.id`.
 * - Dotted paths (e.g., 'vendorProperties.google.status') resolve to JSONB extraction:
 *   `d.vendor_properties -> 'google' ->> 'status'`.
 *
 * This is the **single source of truth** for both:
 *   - The whitelist of fields the verify-ids endpoint will accept.
 *   - The path resolver used to build dynamic SELECT clauses.
 *
 * `cdw-grid-configs/device-search.config.ts` references this constant for its
 * `sqlName` properties so grid behavior and backend lookups stay in sync.
 *
 * TODO: extend for non-Google vendors. The JSONB paths below are hardcoded to
 * `vendorProperties.google.*` and reflect today's reality (DEVICE_IMPORT_CONFIG
 * is Google-specific). Multi-vendor support will require either a per-vendor
 * mapping or a `vendor`-aware SELECT (e.g.,
 * `CASE WHEN d.vendor = 'apple' THEN d.vendor_properties->'apple'->>'status' ...`).
 */
export const DEVICE_DB_FIELD_PATHS = {
  id: 'id',
  vendorId: 'vendorId',
  serialNumber: 'serialNumber',
  make: 'make',
  model: 'model',
  osMajorVersion: 'osMajorVersion',
  status: 'vendorProperties.google.status',
  orgUnitPath: 'vendorProperties.google.orgUnitPath',
  annotatedAssetId: 'vendorProperties.google.annotatedAssetId',
  annotatedUser: 'vendorProperties.google.annotatedUser',
  annotatedLocation: 'vendorProperties.google.annotatedLocation',
  notes: 'vendorProperties.google.notes',
} as const;

export type DeviceDbFieldName = keyof typeof DEVICE_DB_FIELD_PATHS;

/**
 * Resolve a client-side filter field name to the dotted DB path saas-device-api
 * expects. Known names route through `DEVICE_DB_FIELD_PATHS`; unknown names
 * fall through to a `vendorProperties.google.<name>` prefix on the assumption
 * that the client is referring to a vendor JSONB property the canonical map
 * doesn't enumerate yet. Defensively idempotent — passing an already-prefixed
 * name like `'vendorProperties.google.status'` collapses the would-be double
 * prefix.
 */
export function toDeviceDbFieldPath(name: string): string {
  if (name in DEVICE_DB_FIELD_PATHS) {
    return DEVICE_DB_FIELD_PATHS[name as DeviceDbFieldName];
  }
  return `vendorProperties.google.${name}`.replace(
    'vendorProperties.google.vendorProperties.google.',
    'vendorProperties.google.'
  );
}

/**
 * Filter-only field overrides (EDUS-2618).
 *
 * Google delivers a single `model` string ("HP Chromebook 14 G7"); the device sync
 * splits it into a `make` column (first token) and a `model` column (the rest) — see
 * google-device-mapper.service.ts. The grid displays the concatenation, so the single
 * "Model" search box has to match the concatenation too: OR-ing two independent column
 * ILIKEs cannot match a full string like "Samsung Chromebox Series 3", because neither
 * column contains all of it.
 *
 * `a+b` is the shared filter engine's composite-field encoding. The engine splits on
 * '+', validates every segment against live entity metadata, and emits one ILIKE over
 * CONCAT_WS of those columns — see TypeORMDataRepository.parseCompositeFieldName.
 *
 * FILTERS ONLY. Projection (`sqlName`, export SELECT clauses, `selectFields`) and
 * sorting still go through DEVICE_DB_FIELD_PATHS, which is deliberately left alone.
 */
export const DEVICE_FILTER_FIELD_OVERRIDES: Readonly<Record<string, string>> = {
  model: 'make+model',
};

/**
 * Resolve a client filter field name to the path the device API should filter on.
 * Identical to `toDeviceDbFieldPath` except for the composite overrides above.
 */
export function toDeviceFilterFieldPath(name: string): string {
  return DEVICE_FILTER_FIELD_OVERRIDES[name] ?? toDeviceDbFieldPath(name);
}

/**
 * Rewrite each filter's `field.name` from the client's short form (e.g.
 * `'status'`) to the dotted JSONB path saas-device-api / TypeORM expects
 * (e.g. `'vendorProperties.google.status'`). Lines whose names already
 * resolve to themselves (top-level columns) pass through by reference so
 * the all-top-level common case allocates only the outer array.
 *
 * Generic over the filter shape so callers using `IFilterOption[]` (GET
 * path) and `CdwGridFilter[]` (selection-mutation path) share one
 * implementation — both interfaces expose `field.name`, which is all
 * this transform reads.
 */
export function mapFilterFieldNamesForDeviceApi<
  T extends { field: { name: string } },
>(filter: readonly T[]): T[] {
  return filter.map((f) => {
    const mapped = toDeviceFilterFieldPath(f.field.name);
    return mapped === f.field.name
      ? f
      : { ...f, field: { ...f.field, name: mapped } };
  });
}
